import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Boxes, PackagePlus, PackageMinus, Factory,
  Workflow, FileBarChart, Settings as SettingsIcon, CreditCard,
  Layers3, ScanLine, ChevronsLeft, ChevronsRight, LogOut, ShieldCheck,
  ShoppingCart, Truck, ClipboardCheck, TrendingUp, Building2, FlaskConical,
  Beaker, Upload, Calendar,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { OperonLogo } from "@/components/OperonLogo";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const NAV_GROUPS = [
  { title: "Operations", items: [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/app/inventory", label: "Inventory", icon: Boxes },
    { to: "/app/inventory/import", label: "Import inventory", icon: Upload },
    { to: "/app/intake", label: "Goods Intake", icon: PackagePlus },
    { to: "/app/outtake", label: "Goods Outtake", icon: PackageMinus },
    { to: "/app/scanner", label: "Barcode Scan", icon: ScanLine },
  ]},
  { title: "Supply Chain", items: [
    { to: "/app/purchases", label: "Purchases", icon: ShoppingCart },
    { to: "/app/suppliers", label: "Suppliers", icon: Truck },
    { to: "/app/transfers", label: "Transfers", icon: Workflow },
    { to: "/app/qc", label: "Quality Control", icon: ClipboardCheck },
  ]},
  { title: "Manufacturing", items: [
    { to: "/app/production", label: "Production", icon: Factory },
    { to: "/app/production-calendar", label: "Calendar", icon: Calendar },
    { to: "/app/tanks", label: "Tanks / Batches", icon: Beaker },
    { to: "/app/bom", label: "Size BOM", icon: Layers3 },
  ]},
  { title: "Insights", items: [
    { to: "/app/reports", label: "Reports", icon: FileBarChart },
    { to: "/app/reports/supplier-activity", label: "Supplier Activity", icon: Truck },
    { to: "/app/analytics", label: "Advanced Analytics", icon: TrendingUp },
    { to: "/app/lots", label: "Lot Reporting", icon: FlaskConical },
  ]},
];

const SECONDARY = [
  { to: "/app/locations", label: "Locations", icon: Building2 },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon },
  { to: "/app/billing", label: "Billing", icon: CreditCard },
];

export function Sidebar({ onClose }) {
  const { company, user, logout } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("operon.sidebarCollapsed") === "1");

  useEffect(() => {
    localStorage.setItem("operon.sidebarCollapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => { onClose?.(); }, [location.pathname]);

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        "h-dvh sticky top-0 border-r border-border bg-card flex flex-col shrink-0",
        collapsed ? "w-[76px]" : "w-[272px]"
      )}
    >
      <div className={cn("flex items-center gap-2 px-4 h-16 border-b border-border", collapsed ? "justify-center px-0" : "")}>
        <OperonLogo theme={theme} size={collapsed ? 30 : 34} customLogo={theme === "light" ? company?.custom_logo_base64 : null} companyName={company?.name} />
      </div>

      {!collapsed && (
        <div className="px-4 pt-4 pb-2">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-heading">Workspace</div>
          <div className="mt-1 flex items-center gap-2 text-sm font-medium truncate">
            <ShieldCheck className="w-3.5 h-3.5 text-primary" />
            <span className="truncate" data-testid="workspace-company-name">{company?.name || "Operon"}</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5 capitalize">
            {company?.plan?.replace("_", " ") || "free trial"} · <span className="text-emerald-500">{company?.subscription_status || ""}</span>
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-2 py-2">
        <TooltipProvider delayDuration={100}>
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi} className={gi === 0 ? "" : "mt-4"}>
              <div className={cn("mb-1", collapsed ? "" : "px-2 text-[10px] uppercase tracking-wider text-muted-foreground font-heading")}>
                {collapsed ? null : group.title}
              </div>
              <ul className="space-y-1">
                {group.items.map(({ to, label, icon: Icon }) => (
                  <li key={to}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <NavLink
                          to={to}
                          data-testid={`sidebar-nav-item-${to.replace("/app/", "")}`}
                          className={cn(
                            "relative group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150",
                            isActive(to)
                              ? "bg-accent text-foreground ring-1 ring-primary/20 before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[2px] before:bg-primary before:rounded-full"
                              : "text-muted-foreground hover:bg-accent/70 hover:text-foreground"
                          )}
                        >
                          <Icon className={cn("w-[18px] h-[18px] shrink-0", isActive(to) ? "text-primary" : "")} />
                          {!collapsed && <span className="truncate">{label}</span>}
                        </NavLink>
                      </TooltipTrigger>
                      {collapsed && <TooltipContent side="right">{label}</TooltipContent>}
                    </Tooltip>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className={cn("mt-4 mb-1", collapsed ? "" : "px-2 text-[10px] uppercase tracking-wider text-muted-foreground font-heading")}>
            {collapsed ? null : "Account"}
          </div>
          <ul className="space-y-1">
            {SECONDARY.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <NavLink to={to} data-testid={`sidebar-nav-item-${to.replace("/app/", "")}`}
                      className={cn(
                        "relative group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150",
                        isActive(to)
                          ? "bg-accent text-foreground ring-1 ring-primary/20"
                          : "text-muted-foreground hover:bg-accent/70 hover:text-foreground"
                      )}>
                      <Icon className={cn("w-[18px] h-[18px] shrink-0", isActive(to) ? "text-primary" : "")} />
                      {!collapsed && <span className="truncate">{label}</span>}
                    </NavLink>
                  </TooltipTrigger>
                  {collapsed && <TooltipContent side="right">{label}</TooltipContent>}
                </Tooltip>
              </li>
            ))}
          </ul>
        </TooltipProvider>
      </nav>

      <div className="border-t border-border p-3">
        <div className={cn("flex items-center", collapsed ? "justify-center flex-col gap-2" : "justify-between gap-2") }>
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-xs font-medium truncate">{user?.full_name || user?.email}</div>
              <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
            </div>
          )}
          <div className={cn("flex items-center gap-1", collapsed ? "flex-col" : "") }>
            <Button variant="ghost" size="icon" onClick={logout} title="Log out" data-testid="sidebar-logout-button" className="h-8 w-8"><LogOut className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => setCollapsed((c) => !c)} title={collapsed ? "Expand" : "Collapse"} data-testid="sidebar-collapse-button" className="h-8 w-8 hidden lg:inline-flex">
              {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </aside>
  );
}
